import { Calendar, Download, FileText, Plus } from 'lucide-react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import Button from '../../components/Common/Button';
import BoostATSAnalyzing from '../../components/ResumeBuilder/BoostATSAnalyzing';
import BoostATSCard from '../../components/ResumeBuilder/BoostATSCard';
import BoostATSModal from '../../components/ResumeBuilder/BoostATSModal';
import BoostATSResults, {
  BoostATSResultData
} from '../../components/ResumeBuilder/BoostATSResults';
import ResumeBuilderMethod from '../../components/ResumeBuilder/ResumeBuilderMethod';
import ResumeDetailsPage from '../../components/ResumeBuilder/ResumeDetailsPage';
import ResumePreviewLayout from '../../components/ResumeBuilder/ResumePreviewLayout';
import Stepper from '../../components/ResumeBuilder/Stepper';
import { resetResume, setResume } from '../../redux/Slices/ResumeSlice';
import {
  useAnalyseATSMutation,
  useLazyFetchResumeByIdQuery,
  useFetchResumesByUserQuery,
  useScrapeResumeForATSMutation,
  useUpdateResumeMutation
} from '../../services/resumeBuilderApi';
import TemplateSelectionPage from '../TemplateSelectionPage';

const ResumeCard = ({
  title,
  date,
  status,
  renderedHtml,
  onClick,
  onDownload
}: {
  title: string;
  date: string;
  status: string;
  renderedHtml?: string;
  onClick: () => void;
  onDownload: (e: React.MouseEvent) => void;
}) => {
  return (
    <div className='group w-[220px] cursor-pointer overflow-hidden rounded-xl bg-gamma shadow-sm transition hover:-translate-y-1 hover:shadow-md'>
      {/* Preview area */}
      <div className='relative h-[280px] overflow-hidden bg-white'>
        {renderedHtml ? (
          <div
            className='pointer-events-none h-[1120px] w-[794px] origin-top-left select-none'
            style={{ transform: 'scale(0.277)' }}
            dangerouslySetInnerHTML={{ __html: renderedHtml }}
          />
        ) : (
          <div className='flex h-full w-full flex-col items-center justify-center gap-3 bg-gray-50'>
            <FileText size={40} className='text-beta' />
            <span className='text-xs text-gray-400'>No preview available</span>
          </div>
        )}

        {/* Download hover overlay */}
        {renderedHtml && (
          <div className='absolute inset-0 flex items-center justify-center bg-black/5 opacity-0 transition-opacity group-hover:opacity-100'>
            <button
              onClick={onDownload}
              title='Download as PDF'
              className='rounded-full bg-white p-3 shadow-lg transition-transform hover:scale-110 hover:bg-brand hover:text-white'
            >
              <Download size={18} />
            </button>
          </div>
        )}
      </div>

      {/* Card footer */}
      <div className='border-t border-gray-100 p-3'>
        <h3 className='truncate text-sm font-bold'>{title}</h3>

        <div className='mt-1.5 flex items-center justify-between text-xs text-gray-500'>
          <div className='flex items-center gap-1'>
            <Calendar size={12} />
            {date}
          </div>

          <span
            className={`rounded px-2 py-0.5 text-[10px] font-bold ${
              status === 'Completed'
                ? 'bg-green-100 text-green-600'
                : 'bg-blue-100 text-blue-600'
            }`}
          >
            {status}
          </span>
        </div>
      </div>
    </div>
  );
};

const ResumeBuilderMainComponent = () => {
  const dispatch = useDispatch();
  const resume = useSelector((state: any) => state.root.resume);

  const [view, setView] = useState<
    'dashboard' | 'builder' | 'details' | 'template' | 'preview'
  >('dashboard');
  const [updateResume] = useUpdateResumeMutation();
  const [fetchResumeById] = useLazyFetchResumeByIdQuery();
  const [scrapeResumeForATS] = useScrapeResumeForATSMutation();
  const [analyseATS] = useAnalyseATSMutation();

  const {
    data: fetchedResumes,
    isLoading: isLoadingResumes,
    refetch: refetchResumes
  } = useFetchResumesByUserQuery(undefined, {
    refetchOnMountOrArgChange: true
  });

  const resumes: any[] = fetchedResumes?.data || [];

  /* ---- Boost ATS flow state ---- */
  const [boostKey, setBoostKey] = useState(0);
  const [boostScreen, setBoostScreen] = useState<
    'closed' | 'modal' | 'analyzing' | 'results'
  >('closed');
  const [boostResults, setBoostResults] = useState<BoostATSResultData>({
    atsScore: 0,
    atsLevel: '',
    potentialAtsScoreAfterOptimization: 0,
    estimatedScoreIncrease: 0,
    keywordMatchPercentage: 0,
    matchedKeywords: [],
    criticalMissingKeywords: [],
    recommendedKeywords: [],
    sectionScores: {
      skills: 0,
      experience: 0,
      projects: 0,
      education: 0,
      certifications: 0
    },
    strengths: [],
    weaknesses: [],
    improvementSuggestions: [],
    overallAnalysis: ''
  });
  const [boostStep, setBoostStep] = useState(0);

  const handleBoostATSSubmit = async (data: {
    file: File;
    jobRole: string;
    jobDescription: string;
  }) => {
    setBoostStep(0);
    setBoostScreen('analyzing');

    try {
      const formData = new FormData();
      formData.append('file', data.file, data.file.name);

      let resumeId: string;
      try {
        const uploadResponse = await scrapeResumeForATS(formData).unwrap();
        resumeId = uploadResponse?.data?.id;
        setBoostStep(1);
      } catch (error: any) {
        console.error('Resume upload failed:', error);
        toast.error(
          error?.data?.message ||
            'Failed to extract resume data. Please try again.'
        );
        setBoostScreen('modal');
        return;
      }

      try {
        const atsResponse = await analyseATS({
          resumeId,
          jobTitle: data.jobRole,
          jobDescription: data.jobDescription
        }).unwrap();
        setBoostStep(2);

        const result = atsResponse?.data;
        setBoostResults({
          atsScore: result?.atsScore ?? 0,
          atsLevel: result?.atsLevel ?? '',
          potentialAtsScoreAfterOptimization:
            result?.potentialAtsScoreAfterOptimization ?? 0,
          estimatedScoreIncrease: result?.estimatedScoreIncrease ?? 0,
          keywordMatchPercentage: result?.keywordMatchPercentage ?? 0,
          matchedKeywords: result?.matchedKeywords ?? [],
          criticalMissingKeywords: result?.criticalMissingKeywords ?? [],
          recommendedKeywords: result?.recommendedKeywords ?? [],
          sectionScores: result?.sectionScores ?? {
            skills: 0,
            experience: 0,
            projects: 0,
            education: 0,
            certifications: 0
          },
          strengths: result?.strengths ?? [],
          weaknesses: result?.weaknesses ?? [],
          improvementSuggestions: result?.improvementSuggestions ?? [],
          overallAnalysis: result?.overallAnalysis ?? ''
        });

        setBoostScreen('results');
      } catch (error: any) {
        console.error('ATS analysis failed:', error);
        toast.error(
          error?.data?.message || 'ATS analysis failed. Please try again.'
        );
        setBoostScreen('modal');
      }
    } catch (error) {
      console.error('Boost ATS analysis failed:', error);
      toast.error('Something went wrong. Please try again.');
      setBoostScreen('modal');
    }
  };

  const handleTryAnother = () => {
    setBoostScreen('modal');
  };

  const handleCloseBoost = () => {
    setBoostScreen('closed');
    setBoostKey(prev => prev + 1);
  };

  const getStep = () => {
    switch (view) {
      case 'builder':
        return 1;
      case 'details':
        return 2;
      case 'template':
        return 3;
      case 'preview':
        return 4;
      default:
        return 0;
    }
  };

  /* ---- handle creating a new resume ---- */
  const handleCreateNewResume = () => {
    dispatch(resetResume());
    setView('builder');
  };

  const handleContinueDraft = (draft: any) => {
    if (!draft) return;

    dispatch(setResume(draft));
    const savedFormState = draft.resumeFormState;
    if (draft.resumeStatus === 'INITIAL_AI_INTERACTION_COMPLETED') {
      setView('preview');
    } else if (savedFormState?.currentStep && !draft.resumeGenerated) {
      setView('details');
    } else {
      setView('builder');
    }
  };

  /* ---- handle resuming an existing resume ---- */
  const handleResumeClick = async (resumeId: string) => {
    try {
      const response = await fetchResumeById(resumeId).unwrap();
      if (response?.data) {
        dispatch(setResume(response.data));

        const savedFormState = response.data.resumeFormState;
        if (savedFormState?.currentStep && !response.data.resumeGenerated) {
          setView('details');
        } else if (response.data.resumeGenerated) {
          setView('preview');
        } else {
          setView('builder');
        }
      }
    } catch (error) {
      console.error('Failed to fetch resume:', error);
    }
  };

  /* ---- determine resume status for card display ---- */
  const getResumeStatus = (item: any) => {
    if (item.resumeStatus === 'RESUME_GENERATION_COMPLETED') return 'Completed';
    if (item.resumeFormState) return 'In Progress';
    return 'Draft';
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  /* ---- download a single resume as PDF ---- */
  const handleDownloadResume = async (e: React.MouseEvent, item: any) => {
    e.stopPropagation();

    if (!item.renderedHtml) return;

    try {
      const html2pdf = (await import('html2pdf.js')).default;

      /*  html2canvas cannot capture elements with opacity:0 or positioned
          off-screen. Instead we use a hidden iframe — its internal document
          is a fully rendered page that html2canvas can capture reliably.
          This mirrors the proven pattern from ResumePreviewLayout. */
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.top = '0';
      iframe.style.left = '0';
      iframe.style.width = '794px';
      iframe.style.height = '1123px';
      iframe.style.zIndex = '-9999';
      iframe.style.border = 'none';
      document.body.appendChild(iframe);

      const iframeDoc =
        iframe.contentDocument || iframe.contentWindow?.document;
      if (!iframeDoc) {
        document.body.removeChild(iframe);
        return;
      }

      iframeDoc.open();
      iframeDoc.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <style>
              body { margin: 0; padding: 0; background: #fff; }
            </style>
          </head>
          <body>${item.renderedHtml}</body>
        </html>
      `);
      iframeDoc.close();

      /* Wait for iframe content to fully render */
      await new Promise(resolve => setTimeout(resolve, 500));

      const source = iframeDoc.body;
      const fileName = `${item.title || 'Resume'}_${item.personalInfo?.fullName || 'download'}.pdf`;

      const opt = {
        margin: 0.4,
        filename: fileName,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: {
          unit: 'in',
          format: 'letter',
          orientation: 'portrait' as const
        }
      };

      await html2pdf().set(opt).from(source).save();
      document.body.removeChild(iframe);
    } catch (err) {
      console.error('Failed to download resume as PDF:', err);
    }
  };

  const handleSubmit = async () => {
    try {
      const iframe = document.getElementById(
        'resume-iframe'
      ) as HTMLIFrameElement;
      const htmlContent = iframe?.contentWindow?.document?.body?.innerHTML;
      const body = {
        ...resume,
        resumeStatus: 'RESUME_GENERATION_COMPLETED',
        renderedHtml: htmlContent
      };
      await updateResume(body).unwrap();
      refetchResumes();
      setView('dashboard');
    } catch (error) {
      console.error('Error updating resume:', error);
    }
  };

  return (
    <>
      {view === 'dashboard' && (
        <div className='bg-background min-h-screen'>
          <div className='mx-auto w-full max-w-6xl px-4 py-6 lg:px-8'>
            {/* Boost ATS Card */}
            <BoostATSCard
              key={boostKey}
              onGetStarted={() => setBoostScreen('modal')}
            />

            {/* Header */}
            <div className='mb-8 flex items-center justify-between'>
              <div>
                <h2 className='text-2xl font-extrabold'>My Resumes</h2>
                <p className='text-sm text-gray-500'>
                  Manage and track your resumes.
                </p>
              </div>

              <div className='flex items-center gap-2'>
                <Button
                  variant='contained'
                  label='Create New Resume'
                  handleClickBtn={handleCreateNewResume}
                  classes='!ml-0 !py-2 !px-4 !text-sm'
                >
                  <Plus size={16} />
                </Button>
              </div>
            </div>

            {/* Loading state */}
            {isLoadingResumes && (
              <div className='flex min-h-[300px] items-center justify-center'>
                <div className='text-sm text-gray-400'>Loading resumes...</div>
              </div>
            )}

            {/* Resume Grid */}
            {!isLoadingResumes && (
              <div className='flex flex-wrap gap-6'>
                {resumes.length > 0 ? (
                  resumes.map((item: any) => (
                    <ResumeCard
                      key={item.id}
                      title={item.title || 'Untitled Resume'}
                      date={formatDate(item.updatedDate || item.createdDate)}
                      status={getResumeStatus(item)}
                      renderedHtml={item.renderedHtml}
                      onClick={() => handleResumeClick(item.id)}
                      onDownload={e => handleDownloadResume(e, item)}
                    />
                  ))
                ) : (
                  /* Empty state */
                  <button
                    onClick={handleCreateNewResume}
                    className='flex min-h-[200px] w-[220px] flex-col items-center justify-center rounded-xl border-2 border-dashed border-delta hover:bg-white'
                  >
                    <div className='mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-gamma'>
                      <Plus size={18} className='text-brand' />
                    </div>

                    <p className='text-sm font-semibold'>
                      Start creating resume
                    </p>
                    <p className='text-xs text-gray-500'>
                      Build your first resume
                    </p>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Builder (Step 1) */}
      {view === 'builder' && (
        <div className='flex flex-1 flex-col bg-gamma'>
          <div className='mx-auto w-full max-w-5xl px-4 pb-10 pt-4 lg:px-8 lg:pb-12 lg:pt-4'>
            <div className='mx-auto mb-8 mt-4 w-full max-w-4xl'>
              <Stepper currentStep={getStep()} />
            </div>

            {/* Content */}
            <div className='mx-auto mt-6 w-full max-w-4xl'>
              <ResumeBuilderMethod
                onNext={() => setView('details')}
                onContinueDraft={handleContinueDraft}
              />
            </div>
          </div>
        </div>
      )}

      {/* Details (Step 2) */}
      {view === 'details' && (
        <div className='bg-background min-h-screen'>
          <div className='bg-background mx-auto w-full max-w-5xl px-4 pb-10 pt-4 lg:px-8 lg:pb-12  lg:pt-4'>
            <div className='mx-auto mb-8 mt-4 w-full max-w-4xl'>
              <Stepper currentStep={getStep()} />
            </div>

            {/* Content */}
            <div className='mx-auto mt-6 w-full max-w-4xl'>
              <ResumeDetailsPage
                onNext={() => setView('template')}
                onBack={() => setView('builder')}
              />
            </div>
          </div>
        </div>
      )}

      {/* Template (Step 3) - placeholder */}
      {view === 'template' && (
        <div className='flex flex-1 flex-col bg-gamma'>
          <div className='mx-auto w-full max-w-5xl px-4 pb-10 pt-4 lg:px-8 lg:pb-12 lg:pt-4'>
            <div className='mx-auto mb-8 mt-4 w-full max-w-4xl'>
              <Stepper currentStep={getStep()} />
            </div>

            <div className='mx-auto mt-6 w-full max-w-4xl'>
              <TemplateSelectionPage
                onNext={() => setView('preview')}
                onBack={() => setView('details')}
              />
            </div>
          </div>
        </div>
      )}

      {/* Preview (Step 4) - placeholder */}
      {view === 'preview' && (
        <div className='min-h-screen bg-gamma'>
          <div className='mx-auto w-full bg-gamma px-4 pb-10 pt-4 lg:px-8 lg:pb-12  lg:pt-4'>
            <div className='mx-auto mb-8 mt-4 w-full max-w-4xl'>
              <Stepper currentStep={getStep()} />
            </div>

            <div className='mx-auto mt-6 w-full text-center'>
              <ResumePreviewLayout
                onSubmit={handleSubmit}
                handleBack={() => setView('template')}
              />
            </div>
          </div>
        </div>
      )}

      {/* Boost ATS Modals */}
      <BoostATSModal
        key={boostKey}
        isOpen={boostScreen === 'modal'}
        close={handleCloseBoost}
        onSubmit={handleBoostATSSubmit}
      />
      <BoostATSAnalyzing
        isOpen={boostScreen === 'analyzing'}
        completedStep={boostStep}
      />
      <BoostATSResults
        isOpen={boostScreen === 'results'}
        close={handleCloseBoost}
        data={boostResults}
        onTryAnother={handleTryAnother}
      />
    </>
  );
};

export default ResumeBuilderMainComponent;
